import { Box, Icons, useTheme } from '@ublend-npm/aulaui-core';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { goToSubmissions } from '@app/actions/navigation';
import {
  getSelectedAssignment,
  selectCurrentAssignmentId,
} from '@core/assignments/selectors';
import { getCurrentSpaceId } from '@core/selectors/space';
import { institutionShortName } from '@core/selectors/institution';
import { AulaState } from '@core/types/state';
import { MANUAL, QUIZZES_PROVIDER } from '@core/assignments/constants';
import { createStyles } from './AssignmentDetails.styles';
import NoResultsCard from '@app/components/presentational/common/NoResults/NoResultsCard';
import { emptyAssignmentDetailsText } from '../../../../../../app/constants/texts';
import { ResponsiveAssignment } from '../types';
import AssignmentHeader from '../AssignmentHeader/AssignmentHeader';
import { accessingCurrentSpaceAsStaff } from '@core/selectors/classroom';
import { accessToken as getAccessToken } from '@core/selectors/user';
import { operations as assignmentOperations } from '@app/store/assignments';
import openGroups from '@app/actions/navigation/openGroups.action';
import QuizDecommissionBanner from '../SubmissionsResponsive/QuizDecommissionBanner';
import { UWS_INSTITUTIONS, COVENTRY_INSTITUTIONS } from '../constants';
import GroupFilter, { GroupFilterSet } from '../GroupFilter/GroupFilter';

interface AssignmentDetailsProps {
  assignments: ResponsiveAssignment[];
  currentUserRole: 'educator' | 'staff' | 'student';
}

type FetchSubmissionsAction = (params: {
  accessToken: string;
  assignmentId: string;
  group?: string | null;
}) => ReturnType<typeof assignmentOperations.fetchSubmissions>;

const typedFetchSubmissions =
  assignmentOperations.fetchSubmissions as unknown as FetchSubmissionsAction;

const AssignmentDetails = ({
  assignments,
  currentUserRole = 'student',
}: AssignmentDetailsProps) => {
  const theme = useTheme();
  const styles = createStyles(theme);
  const dispatch = useDispatch();
  const spaceId = useSelector((state: AulaState) => getCurrentSpaceId(state));
  const assignmentId = useSelector(selectCurrentAssignmentId);
  const institution = useSelector(institutionShortName) as string;
  const selectedAssignment = useSelector(getSelectedAssignment);
  const accessToken = useSelector((state: AulaState) =>
    getAccessToken(state, spaceId)
  );
  const groupFilterSets = useSelector((state: AulaState) => {
    const groupData = state.data?.groups;
    if (!groupData) return [] as GroupFilterSet[];

    return Object.values(groupData.sets || {})
      .filter((set: any) => set?.space === spaceId)
      .flatMap((set: any) => [
        { isHeader: true, key: set.id, label: set.name },
        ...(set.groups || [])
          .map((groupId: string) => groupData.groups?.[groupId])
          .filter(Boolean)
          .map((group: any) => ({
            externalId: set.externalId,
            isHeader: false,
            key: group.id,
            label: group.name,
          })),
      ]);
  });
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const accessingAsStaff =
    useSelector(accessingCurrentSpaceAsStaff) || currentUserRole === 'staff';

  const isQuizAssignment = selectedAssignment?.provider === QUIZZES_PROVIDER;
  const quizBannerInstitution =
    institution && UWS_INSTITUTIONS.includes(institution)
      ? 'uws'
      : institution && COVENTRY_INSTITUTIONS.includes(institution)
      ? 'coventry'
      : undefined;

  // Reuses the legacy submissions route:
  // /dashboard/:spaceId/journey/assignments/:assignmentId/submissions
  const handleViewSubmissions = () => {
    if (!spaceId || !assignmentId) {
      return;
    }
    dispatch(goToSubmissions({ spaceId, assignmentId }));
  };

  const handleGroupFilterChange = (groupId: string) => {
    const selectedAssignmentId =
      selectedAssignment?.id || selectedAssignment?.objectId;
    if (!accessToken || !selectedAssignmentId) return;

    setSelectedGroupId(groupId);
    dispatch(
      typedFetchSubmissions({
        accessToken,
        assignmentId: selectedAssignmentId,
        group: groupId || null,
      })
    );
  };

  if (assignments?.length === 0 || !selectedAssignment) {
    return (
      <Box sx={styles.emptyWrapper}>
        <NoResultsCard
          title={emptyAssignmentDetailsText[currentUserRole].title}
          description={emptyAssignmentDetailsText[currentUserRole].description}
          sx={styles.noResultsCard}
          titleSx={styles.noResultsCardTitle}
          descriptionSx={styles.noResultsCardDescription}
          icon={<Icons.FolderOffOutlined sx={{ fontSize: 64 }} aria-hidden />}
        />
      </Box>
    );
  }

  return (
    <Box sx={styles.wrapper}>
      {isQuizAssignment && (
        <Box
          sx={{
            width: { xs: '100%', lg: '792px' },
            maxWidth: { xs: '100%', md: '792px' },
          }}
        >
          <QuizDecommissionBanner institution={quizBannerInstitution} />
        </Box>
      )}
      <AssignmentHeader
        assignment={selectedAssignment}
        page="overview"
        handleViewSubmissions={handleViewSubmissions}
        isEducator={currentUserRole === 'educator'}
        isStudent={currentUserRole === 'student'}
        accessingAsStaff={accessingAsStaff}
        currentSpaceId={spaceId}
      />
      {selectedAssignment.provider !== MANUAL &&
        (currentUserRole === 'educator' || accessingAsStaff) && (
          <Box sx={styles.groupFilterWrapper}>
            <Box sx={styles.groupFilterControl}>
              <GroupFilter
                groups={groupFilterSets}
                onClear={() => handleGroupFilterChange('')}
                onManageGroups={() => dispatch(openGroups(spaceId))}
                onSelectGroup={handleGroupFilterChange}
                value={selectedGroupId}
              />
            </Box>
          </Box>
        )}
    </Box>
  );
};

export default AssignmentDetails;
